// Human readable messages for failed requests.
//
// Before this module FetchRequest reported every failure as showNotification("error")
// with no text at all: an empty toast, nothing in the console, and only the 500 and
// 422 statuses were looked at. Everything a user or a developer sees about a failed
// request is built here.

import type { ApiErrorInit, ApiErrorKind, ServerErrorBody } from "types/api";

// Requests that hang longer than this are aborted, so a dead backend gives a message
// instead of a spinner that never stops.
export const REQUEST_TIMEOUT_MS = 60000;

export const ERROR_KINDS: Record<ApiErrorKind, ApiErrorKind> = {
  http: "http", // server answered 4xx/5xx
  validation: "validation", // 422 with a field level error list
  network: "network", // fetch itself failed: host down, DNS, CORS, offline
  timeout: "timeout", // aborted by REQUEST_TIMEOUT_MS
  parse: "parse", // answer claims to be JSON but is not
  unexpected: "unexpected", // a bug in our own response handling
};

export class ApiError extends Error {
  // Checked instead of `instanceof`: the api layer rethrows across module
  // boundaries and CRA 4 transpiles Error subclasses for older targets, where
  // instanceof on a subclassed Error is not reliable.
  readonly isApiError = true;

  readonly kind: ApiErrorKind;

  readonly status: number;

  readonly method: string;

  readonly url: string;

  readonly body: unknown;

  constructor(
    message: string,
    { kind = ERROR_KINDS.http, status = 0, method = "", url = "", body = null }: ApiErrorInit = {}
  ) {
    super(message);
    this.name = "ApiError";
    this.kind = kind;
    this.status = status;
    this.method = method;
    this.url = url;
    this.body = body;
  }
}

// The one check the whole app does on a rejection: everything the api layer throws has
// already been reported, anything else is a bug worth logging.
export const isApiError = (error: unknown): error is ApiError =>
  Boolean(error) && (error as ApiError).isApiError === true;

// Our own wording per status, used when the backend says nothing useful.
const STATUS_MESSAGES: Record<number, string> = {
  400: "Bad request: the server rejected the payload.",
  401: "Not authorized: the token is missing, expired or belongs to another backend. Sign in again.",
  403: "Access denied: this token may not perform the action.",
  404: "Not found: this backend has no such endpoint.",
  405: "Method not allowed for this endpoint.",
  409: "Conflict: the record was changed or already exists.",
  413: "Payload too large: the file is over the server limit.",
  419: "Session expired. Sign in again.",
  422: "Validation failed, but the server did not say which field.",
  429: "Too many requests. Wait a moment and retry.",
  500: "Internal server error.",
  502: "Bad gateway: the backend is unreachable.",
  503: "Service unavailable: the backend is down or restarting.",
  504: "Gateway timeout: the backend did not answer in time.",
};

// The backend replies in several shapes depending on the endpoint:
//   { message: "..." }                        exceptions and abort()
//   { errors: { field: ["msg", ...] } }        validator
//   { errors: ["msg"] } / { messages: {...} }  hand written responses
export function extractServerMessages(body: unknown): string[] {
  if (!body) return [];

  // A plain text body is usually an HTML debug page or a stack trace: too long to
  // put in a toast. Only a short, clearly textual answer is worth showing.
  if (typeof body === "string") {
    const text = body.trim();
    return text && text.length <= 200 && !text.startsWith("<") ? [text] : [];
  }

  const found: string[] = [];
  const push = (value: unknown): void => {
    if (typeof value === "string" && value.trim()) found.push(value.trim());
    else if (Array.isArray(value)) value.forEach(push);
  };

  const source = body as ServerErrorBody;

  push(source.message);
  push(source.error);
  [source.errors, source.messages].forEach((bag) => {
    if (!bag || typeof bag !== "object") return push(bag);
    if (Array.isArray(bag)) return bag.forEach(push);
    return Object.keys(bag).forEach((key) => push((bag as Record<string, unknown>)[key]));
  });

  return found.filter((message, index) => found.indexOf(message) === index);
}

// "POST /api/users/get" — what is worth reading in a toast, without the domain.
export function describeRequest(method: string, url: string): string {
  let path = url || "";
  try {
    path = new URL(url).pathname;
  } catch (error) {
    // relative or malformed url, keep it as it is
  }
  return `${method || "REQUEST"} ${path}`;
}

export function httpErrorText(status: number, method: string, url: string, body: unknown): string {
  const messages = extractServerMessages(body);

  // Validation errors are read by admins all day long, so they stay exactly as
  // they were: the field messages and nothing else.
  if (status === 422 && messages.length) return messages.join("\n");

  const explanation =
    STATUS_MESSAGES[status] || (status >= 500 ? "Server error." : "The server rejected the request.");

  return [`HTTP ${status} · ${describeRequest(method, url)}`, messages.length ? messages.join("\n") : explanation].join(
    "\n"
  );
}

export function networkErrorText(method: string, url: string): string {
  return [
    `Network error · ${describeRequest(method, url)}`,
    "The backend did not answer. Check that the domain from the sign in screen is correct and reachable, and that it allows CORS for this origin.",
  ].join("\n");
}

export function timeoutErrorText(method: string, url: string, timeout: number): string {
  const waited = timeout >= 1000 ? `${Math.round(timeout / 1000)}s` : `${timeout}ms`;

  return [
    `Timeout after ${waited} · ${describeRequest(method, url)}`,
    "The backend did not answer in time, the request was aborted.",
  ].join("\n");
}

export function parseErrorText(status: number, method: string, url: string): string {
  return [
    `Malformed answer · ${describeRequest(method, url)}`,
    `The server replied ${status} with a Content-Type of application/json, but the body is not valid JSON.`,
  ].join("\n");
}

// fetch() rejects with TypeError when it never reached the server, and with an
// AbortError when our own timeout fired.
export function toTransportError(error: unknown, method: string, url: string, timeout: number): ApiError {
  if (isApiError(error)) return error;

  const failure = error as { name?: string; code?: number; message?: string } | null;
  const isAbort = Boolean(failure && (failure.name === "AbortError" || failure.code === 20));
  const kind = isAbort ? ERROR_KINDS.timeout : ERROR_KINDS.network;
  const text = isAbort ? timeoutErrorText(method, url, timeout) : networkErrorText(method, url);

  return new ApiError(text, { kind, method, url, body: failure && failure.message ? failure.message : null });
}

// A failure in our own response handling, not in the request: still must not be silent.
export function toUnexpectedError(error: unknown, method: string, url: string): ApiError {
  if (isApiError(error)) return error;

  const failure = error as { message?: string } | null;
  const detail = failure && failure.message ? failure.message : String(error);

  return new ApiError([`Unexpected error · ${describeRequest(method, url)}`, detail].join("\n"), {
    kind: ERROR_KINDS.unexpected,
    method,
    url,
  });
}
